import { useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { NoBackend } from "@/components/common/NoBackend";
import { BoxHeader } from "@/components/design/BoxHeader";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { EditPanelButtons } from "@/components/design/EditPanelButtons";
import { EntryFields } from "@/components/design/EntryFields";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { isFieldMissing } from "@/lib/field-util";
import type { FieldDef, FieldOption } from "@/lib/field-util";
import type { Audience } from "@/lib/audience";
import { useAutoCloseOnSave } from "@/lib/use-auto-close-on-save";
import { buildRoles, type EntryShape, type RoleEntry } from "./career-graph-lanes";

// Career & Education from real backend data (myself.experiences +
// .educations). Graph mode = git-graph style: trunk (mainjob) + branch
// per role, latest lane innermost (no crossings), fork from the trunk,
// merge/HEUTE arrows. List mode = compact list. Without entries: unified
// empty state.
//
// On the user's own profile (editable), clicking an entry opens the
// same form (EntryFields) as adding - identical in list AND graph.
// Ported node-by-node from workshop/ui/CareerGraph.jsx.

function vis(
  sharedWith: ReadonlyArray<Audience> | null | undefined,
  labels: { private: string; public: string; shared: string },
): string {
  const list = sharedWith ?? [];
  if (list.length === 0) return labels.private;
  if (list.some((a) => a?.isPublic)) return labels.public;
  const names = list
    .map((a) => a?.name?.localized || a?.name?.english)
    .filter((n): n is string => Boolean(n));
  return names.length ? names.join(", ") : labels.shared;
}

function fmt(v: string | null | undefined, intl: IntlShape): string {
  if (!v) return "";
  const m = String(v).match(/(\d{4})-(\d{2})/);
  if (m) {
    // Locale-correct short month + year (e.g. "Mär 2020" / "Mar 2020").
    return intl.formatDate(new Date(Number(m[1]), Number(m[2]) - 1, 1), {
      month: "short",
      year: "numeric",
    });
  }
  const y = String(v).match(/\d{4}/);
  return y ? y[0] : String(v);
}

type Mode = "list" | "graph";

interface CareerGraphProps {
  experiences?: readonly EntryShape[] | null;
  educations?: readonly EntryShape[] | null;
  renderShareControl?: (role: RoleEntry) => ReactNode;
  // Share control rendered inside an entry's edit panel. Falls back to
  // renderShareControl; lets the page force it always-visible there while the
  // inline display version stays gated by the global share-edit mode.
  renderShareControlEdit?: (role: RoleEntry) => ReactNode;
  title?: string;
  emptyLabel?: string;
  footer?: ReactNode;
  // Accent ring on the box when it has pending LinkedIn-import changes.
  highlight?: boolean;
  editable?: boolean;
  onPatch?: (kind: "experience" | "education", id: string, patch: Record<string, unknown>) => void;
  /**
   * Drop the row's local overlay (entryEdits slice for existing entries,
   * full draft for new ones) and close the panel. Synthetic draft ids
   * start with "draft-" and are removed entirely from entryDrafts.
   */
  onCancel?: (kind: "experience" | "education", id: string) => void;
  /**
   * Stage the row for deletion on the next SaveBar commit. Not called
   * for drafts - ABBRECHEN handles those. When omitted, the Löschen
   * button is not rendered on any row.
   */
  onDelete?: (kind: "experience" | "education", id: string) => void;
  editFields?: readonly FieldDef[];
  countryOptions?: readonly FieldOption[];
  industryOptions?: readonly FieldOption[];
  /** Synthetic ids (starts with "draft-") of newly-added rows not yet on the backend. */
  draftIds?: ReadonlySet<string>;
}

export function CareerGraph({
  experiences,
  educations,
  renderShareControl,
  renderShareControlEdit,
  title,
  emptyLabel,
  footer,
  highlight = false,
  editable = false,
  onPatch,
  onCancel,
  onDelete,
  editFields,
  countryOptions,
  industryOptions,
  draftIds,
}: CareerGraphProps) {
  const intl = useIntl();
  const resolvedTitle =
    title ??
    intl.formatMessage({ id: "save.careerGraph.title", defaultMessage: "Career & Education" });
  const resolvedEmptyLabel =
    emptyLabel ??
    intl.formatMessage({
      id: "save.careerGraph.emptyLabel",
      defaultMessage: "No career or education entries",
    });
  const [mode, setMode] = useState<Mode>("graph");
  const [editingId, setEditingId] = useState<string | null>(null);
  // Same SaveBar-tick subscription as EditableRow - every Entry edit
  // panel collapses together on a successful commit. Partial failures
  // do not bump the tick, so failed rows stay open.
  useAutoCloseOnSave(() => setEditingId(null));

  const { roles, laneCount } = buildRoles(experiences, educations);

  const countryName = new Map((countryOptions ?? []).map((o) => [o.value, o.label]));
  const industryName = new Map((industryOptions ?? []).map((o) => [o.value, o.label]));
  roles.forEach((r) => {
    r.countryName = r.country ? countryName.get(r.country) ?? "" : "";
    r.industryName = r.industry ? industryName.get(r.industry) ?? "" : "";
  });

  const canEdit = editable && Boolean(onPatch);
  const editProps = {
    editingId: canEdit ? editingId : null,
    onEdit: canEdit ? setEditingId : null,
    onPatch,
    onCancel,
    onDelete,
    editFields,
    renderShareControlEdit,
    draftIds,
  };

  return (
    <div className={`box bg-surface p-5 ${highlight ? "ring-2 ring-accent/40" : ""}`}>
      <BoxHeader
        title={resolvedTitle}
        right={roles.length > 0 ? <ViewToggle mode={mode} onChange={setMode} /> : null}
      />
      {roles.length === 0 ? (
        footer ? null : <NoBackend label={resolvedEmptyLabel} />
      ) : mode === "list" ? (
        <ListView roles={roles} renderShareControl={renderShareControl} {...editProps} />
      ) : (
        <GraphView
          roles={roles}
          laneCount={laneCount}
          renderShareControl={renderShareControl}
          {...editProps}
        />
      )}
      {footer}
    </div>
  );
}

function ViewToggle({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  const intl = useIntl();
  return (
    <SegmentedControl<Mode>
      size="sm"
      value={mode}
      onChange={onChange}
      options={[
        { value: "list", label: intl.formatMessage({ id: "save.careerGraph.modeList", defaultMessage: "Classic" }) },
        { value: "graph", label: intl.formatMessage({ id: "save.careerGraph.modeGraph", defaultMessage: "Modern" }) },
      ]}
    />
  );
}

function SharedDisplay({
  role,
  renderShareControl,
}: {
  role: RoleEntry;
  renderShareControl?: (role: RoleEntry) => ReactNode;
}) {
  const intl = useIntl();
  return renderShareControl ? (
    <>{renderShareControl(role)}</>
  ) : (
    <span className="text-[9px] tracking-[0.15em] text-ink/40">
      {vis(role.shared, {
        private: intl.formatMessage({
          id: "save.careerGraph.visibilityPrivate",
          defaultMessage: "Private",
        }),
        public: intl.formatMessage({
          id: "save.careerGraph.visibilityPublic",
          defaultMessage: "Public",
        }),
        shared: intl.formatMessage({
          id: "save.careerGraph.visibilityShared",
          defaultMessage: "Shared",
        }),
      })}
    </span>
  );
}

function ActiveBadge() {
  return (
    <span className="text-[9px] tracking-[0.2em] text-accent">
      ● <FormattedMessage id="save.careerGraph.active" defaultMessage="Active" />
    </span>
  );
}

// Compact "i" badge for the (height-constrained) graph card: the activities
// list is hidden inline and revealed on hover/focus.
function ActivitiesInfo({ activities }: { activities: string[] }) {
  const intl = useIntl();
  return (
    <InfoTooltip
      ariaLabel={intl.formatMessage({
        id: "save.careerGraph.showMainTasks",
        defaultMessage: "Show main tasks",
      })}
      title={<FormattedMessage id="save.careerGraph.mainTasks" defaultMessage="Main tasks" />}
      placement="bottom"
    >
      <ul className="list-disc space-y-0.5 break-words pl-4 text-left">
        {activities.map((a, i) => (
          <li key={i}>{a}</li>
        ))}
      </ul>
    </InfoTooltip>
  );
}

function CardBody({
  r,
  renderShareControl,
  dense = false,
}: {
  r: RoleEntry;
  renderShareControl?: (role: RoleEntry) => ReactNode;
  // Graph (dense) cards omit the inline activities list (shown via the ⓘ
  // tooltip) and size to their content; the list view shows activities inline.
  dense?: boolean;
}) {
  const intl = useIntl();
  const place = [r.countryName, r.location].filter(Boolean).join(" · ");
  return (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-1.5">
          <div className="text-sm font-medium transition group-hover:text-accent">{r.title}</div>
          {dense && r.activities.length > 0 ? <ActivitiesInfo activities={r.activities} /> : null}
        </div>
        <div className="flex shrink-0 items-baseline gap-2">
          {r.active ? <ActiveBadge /> : null}
          <SharedDisplay role={r} renderShareControl={renderShareControl} />
        </div>
      </div>
      {r.company || r.industryName ? (
        <div className="field-meta text-ink/55">
          {[r.company, r.industryName ? `(${r.industryName})` : ""].filter(Boolean).join(" ")}
        </div>
      ) : null}
      <div className="field-meta text-ink/55">
        {fmt(r.start, intl)} –{" "}
        {r.active ? (
          <FormattedMessage id="save.careerGraph.today" defaultMessage="today" />
        ) : (
          fmt(r.end, intl)
        )}
      </div>
      {place ? <div className="field-meta text-ink/55">{place}</div> : null}
      {!dense && r.activities.length > 0 ? (
        <ul className="field-meta mt-1.5 list-disc space-y-0.5 pl-4 text-ink/55">
          {r.activities.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

interface RoleEditorProps {
  role: RoleEntry;
  onPatch: (kind: "experience" | "education", id: string, patch: Record<string, unknown>) => void;
  editFields?: readonly FieldDef[];
  renderShareControl?: (role: RoleEntry) => ReactNode;
  renderShareControlEdit?: (role: RoleEntry) => ReactNode;
  onClose: () => void;
  /** Drop the row's overlay (entryEdits slice or full draft) then close. */
  onCancel?: () => void;
  /** Stage row for deletion. Caller handles state; we just close. */
  onDelete?: () => void;
  /** Label for the confirm button. "Hinzufügen" for drafts, "Fertig" otherwise. */
  confirmLabel?: string;
}

function RoleEditor({
  role,
  onPatch,
  editFields,
  renderShareControl,
  renderShareControlEdit,
  onClose,
  onCancel,
  onDelete,
  confirmLabel,
}: RoleEditorProps) {
  const intl = useIntl();
  const resolvedConfirmLabel =
    confirmLabel ??
    intl.formatMessage({ id: "save.careerGraph.done", defaultMessage: "Done" });
  const shareControl = renderShareControlEdit ?? renderShareControl;
  const fields = editFields ?? [];
  const entryValues = role.entry as Record<string, unknown>;
  const missing = fields
    .filter((f) => isFieldMissing(f, entryValues?.[f.key]))
    .map((f) => f.label);

  const cancel = () => {
    if (onCancel) onCancel();
    else onClose();
  };

  return (
    <div className="space-y-3 rounded-md bg-page p-3 ring-1 ring-ink/10">
      {shareControl ? (
        <div className="flex justify-end">{shareControl(role)}</div>
      ) : null}
      <EntryFields
        fields={fields}
        values={role.entry}
        onChange={(key, val) => onPatch(role.kind, role.sourceId, { [key]: val })}
      />
      <EditPanelButtons
        onCancel={cancel}
        onConfirm={onClose}
        onDelete={onDelete}
        confirmLabel={resolvedConfirmLabel}
        missingFields={missing}
      />
    </div>
  );
}

function EditPanel({
  role,
  onPatch,
  onCancel,
  onDelete,
  editFields,
  renderShareControl,
  renderShareControlEdit,
  onClose,
  draftIds,
}: {
  role: RoleEntry | null;
  onPatch?: (kind: "experience" | "education", id: string, patch: Record<string, unknown>) => void;
  onCancel?: (kind: "experience" | "education", id: string) => void;
  onDelete?: (kind: "experience" | "education", id: string) => void;
  editFields?: readonly FieldDef[];
  renderShareControl?: (role: RoleEntry) => ReactNode;
  renderShareControlEdit?: (role: RoleEntry) => ReactNode;
  onClose: () => void;
  draftIds?: ReadonlySet<string>;
}) {
  const intl = useIntl();
  if (!role || !onPatch) return null;
  const isDraft = draftIds?.has(role.sourceId) ?? role.sourceId.startsWith("draft-");
  return (
    <div className="mt-4 border-t border-ink/5 pt-4">
      {/* Key on role.id forces remount when switching between entries - the
          inner EntryFields holds context-bound state (AI activity
          suggestions are tied to one role) that must NOT leak across rows. */}
      <RoleEditor
        key={role.id}
        role={role}
        onPatch={onPatch}
        editFields={editFields}
        renderShareControl={renderShareControl}
        renderShareControlEdit={renderShareControlEdit}
        onClose={onClose}
        onCancel={
          onCancel
            ? () => {
                onCancel(role.kind, role.sourceId);
                onClose();
              }
            : undefined
        }
        onDelete={
          onDelete && !isDraft
            ? () => {
                onDelete(role.kind, role.sourceId);
                onClose();
              }
            : undefined
        }
        confirmLabel={
          isDraft
            ? intl.formatMessage({ id: "common.add", defaultMessage: "Add" })
            : intl.formatMessage({ id: "save.careerGraph.done", defaultMessage: "Done" })
        }
      />
    </div>
  );
}

interface ViewProps {
  roles: RoleEntry[];
  renderShareControl?: (role: RoleEntry) => ReactNode;
  renderShareControlEdit?: (role: RoleEntry) => ReactNode;
  editingId: string | null;
  onEdit: ((id: string | null) => void) | null;
  onPatch?: (kind: "experience" | "education", id: string, patch: Record<string, unknown>) => void;
  onCancel?: (kind: "experience" | "education", id: string) => void;
  onDelete?: (kind: "experience" | "education", id: string) => void;
  editFields?: readonly FieldDef[];
  draftIds?: ReadonlySet<string>;
}

function ListView({ roles, renderShareControl, renderShareControlEdit, editingId, onEdit, onPatch, onCancel, onDelete, editFields, draftIds }: ViewProps) {
  const intl = useIntl();
  return (
    <div className="divide-y divide-ink/5">
      {roles.map((r) => {
        const isDraft = draftIds?.has(r.sourceId) ?? r.sourceId.startsWith("draft-");
        return editingId === r.id && onEdit && onPatch ? (
          <div key={r.id} className="py-3 first:pt-0 last:pb-0">
            <RoleEditor
              role={r}
              onPatch={onPatch}
              editFields={editFields}
              renderShareControl={renderShareControl}
              renderShareControlEdit={renderShareControlEdit}
              onClose={() => onEdit(null)}
              onCancel={
                onCancel
                  ? () => {
                      onCancel(r.kind, r.sourceId);
                      onEdit(null);
                    }
                  : undefined
              }
              onDelete={
                onDelete && !isDraft
                  ? () => {
                      onDelete(r.kind, r.sourceId);
                      onEdit(null);
                    }
                  : undefined
              }
              confirmLabel={
                isDraft
                  ? intl.formatMessage({ id: "common.add", defaultMessage: "Add" })
                  : intl.formatMessage({ id: "save.careerGraph.done", defaultMessage: "Done" })
              }
            />
          </div>
        ) : (
          <div
            key={r.id}
            className={`py-3 first:pt-0 last:pb-0 ${onEdit ? "group cursor-pointer" : ""}`}
            onClick={onEdit ? () => onEdit(r.id) : undefined}
          >
            <CardBody r={r} renderShareControl={renderShareControl} />
          </div>
        );
      })}
    </div>
  );
}

// Geometry for the graph: each role = one content-height card row on the right;
// the left gutter is drawn alongside with nodes aligned to each card's title
// line. Card heights are measured at runtime (see GraphView) so the SVG nodes
// track variable-length content; CARD_H is only the pre-measure fallback.
const CARD_H = 84;
const GAP = 14;
const ARROW_TOP = 20;
const TOP_PAD = 42;
const NODE_Y = 11;
const LANE_DX = 18;
const TRUNK_X = 16;
const INK_LINE = "color-mix(in srgb, var(--color-ink) 70%, transparent)";

function Arrow({ x, color = "var(--color-accent)" }: { x: number; color?: string }) {
  return (
    <path
      d={`M ${x - 3} ${ARROW_TOP + 5} L ${x} ${ARROW_TOP} L ${x + 3} ${ARROW_TOP + 5}`}
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
  );
}

function GraphView({
  roles,
  laneCount,
  renderShareControl,
  renderShareControlEdit,
  editingId,
  onEdit,
  onPatch,
  onCancel,
  onDelete,
  editFields,
  draftIds,
}: ViewProps & { laneCount: number }) {
  const laneX = (l: number) => TRUNK_X + l * LANE_DX;

  // Measure each card's rendered height so node/line ys track variable-length
  // content (full text, no truncation). Falls back to CARD_H pre-measure and
  // re-measures on content/width changes via ResizeObserver.
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [heights, setHeights] = useState<number[]>([]);
  useLayoutEffect(() => {
    const measure = () => {
      const next = roles.map((_, i) => cardRefs.current[i]?.offsetHeight || CARD_H);
      setHeights((prev) =>
        prev.length === next.length && prev.every((h, i) => h === next[i]) ? prev : next,
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    cardRefs.current.slice(0, roles.length).forEach((el) => el && ro.observe(el));
    return () => ro.disconnect();
  }, [roles]);

  const cardH = (i: number) => heights[i] ?? CARD_H;
  const ys: number[] = [];
  let acc = TOP_PAD;
  for (let i = 0; i < roles.length; i++) {
    ys.push(acc + NODE_Y);
    acc += cardH(i) + GAP;
  }
  const totalH = acc;
  const graphW = laneX(laneCount - 1) + 12;
  const editingRole = roles.find((r) => r.id === editingId) || null;
  const bottomY = ys[roles.length - 1]!;
  // The lowest branch forks off the trunk at ys[last] + GAP, i.e. below its
  // own node. Unless the last role IS the trunk, extend the trunk that extra
  // GAP so the bottom fork has something to attach to (otherwise it dangles).
  const lastIsBranch = (roles[roles.length - 1]?.lane ?? 0) !== 0;
  const trunkBottomY = lastIsBranch ? bottomY + GAP : bottomY;

  return (
    <>
      <div
        className="relative grid gap-1 min-w-0 overflow-x-auto"
        style={{ gridTemplateColumns: `${graphW}px 1fr`, minHeight: totalH }}
      >
        <div className="relative">
          <div className="absolute left-0 right-0 top-0 flex items-center gap-2 text-[9px] tracking-[0.3em] text-accent">
            <span className="h-[1.5px] flex-1 bg-accent" />
            <span>
              <FormattedMessage id="save.careerGraph.todayMarker" defaultMessage="TODAY" />
            </span>
            <span className="h-[1.5px] flex-1 bg-accent" />
          </div>
          <svg
            width={graphW}
            height={totalH}
            viewBox={`0 0 ${graphW} ${totalH}`}
            className="absolute left-0 top-0"
          >
            {roles.map((r, i) => {
              const x = laneX(r.lane ?? 0);
              const color = r.active ? "var(--color-accent)" : INK_LINE;
              if (r.lane === 0) {
                return (
                  <g key={`lane-${r.id}`}>
                    <line
                      x1={x}
                      y1={r.active ? ARROW_TOP : ys[i]}
                      x2={x}
                      y2={trunkBottomY}
                      stroke={color}
                      strokeWidth="1.5"
                    />
                    {r.active ? <Arrow x={x} /> : null}
                  </g>
                );
              }
              const fork = (
                <path
                  d={`M ${TRUNK_X} ${ys[i]! + GAP} Q ${x} ${ys[i]! + GAP} ${x} ${ys[i]}`}
                  fill="none"
                  stroke={color}
                  strokeWidth="1.5"
                />
              );
              if (r.active) {
                return (
                  <g key={`lane-${r.id}`}>
                    {fork}
                    <line x1={x} y1={ARROW_TOP} x2={x} y2={ys[i]} stroke={color} strokeWidth="1.5" />
                    <Arrow x={x} />
                  </g>
                );
              }
              return (
                <g key={`lane-${r.id}`}>
                  {fork}
                  <path
                    d={`M ${x} ${ys[i]} Q ${x} ${ys[i]! - GAP} ${TRUNK_X} ${ys[i]! - GAP - 4}`}
                    fill="none"
                    stroke={color}
                    strokeWidth="1.5"
                  />
                </g>
              );
            })}

            {roles.map((r, i) => {
              const x = laneX(r.lane ?? 0);
              const color = r.active ? "var(--color-accent)" : INK_LINE;
              return (
                <g key={r.id}>
                  <line
                    x1={x}
                    y1={ys[i]}
                    x2={graphW - 2}
                    y2={ys[i]}
                    stroke={color}
                    strokeOpacity={0.3}
                    strokeWidth="1"
                    strokeDasharray="2 3"
                  />
                  <circle
                    cx={x}
                    cy={ys[i]}
                    r={6}
                    fill={r.active ? "var(--color-accent)" : "var(--color-page)"}
                    stroke={color}
                    strokeWidth="2"
                  />
                </g>
              );
            })}
          </svg>
        </div>

        <div style={{ paddingTop: TOP_PAD }}>
          {roles.map((r, i) => (
            <div
              key={r.id}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              style={{ marginBottom: GAP }}
              className={`relative ${onEdit ? "group cursor-pointer" : ""} ${
                editingId === r.id ? "rounded-md ring-1 ring-inset ring-accent/40" : ""
              }`}
              onClick={onEdit ? () => onEdit(r.id) : undefined}
            >
              <CardBody r={r} renderShareControl={renderShareControl} dense />
            </div>
          ))}
        </div>
      </div>

      <EditPanel
        role={editingRole}
        onPatch={onPatch}
        onCancel={onCancel}
        onDelete={onDelete}
        editFields={editFields}
        renderShareControl={renderShareControl}
        renderShareControlEdit={renderShareControlEdit}
        onClose={() => onEdit?.(null)}
        draftIds={draftIds}
      />
    </>
  );
}
